// File: src/components/pages/MyBookings.jsx
// Student view - Display upcoming bookings with cancellation request functionality

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Calendar, Clock, User, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { bookingAPI, cancellationAPI } from '../../services/api';
import { AUTH0_NAMESPACE } from '../../utils/constants';
import { useStudentAuth } from '../../context/StudentAuthContext';
import { useAuthToken } from '../../hooks/useAuthToken';
import '../../styles/unified.css';

export default function MyBookings() {
  const { user } = useAuth0();
  const { isStudentAuthenticated } = useStudentAuth();
  const getAccessTokenSilently = useAuthToken();

  const userRoles = user?.[`${AUTH0_NAMESPACE}/roles`] || [];
  const isServiceUser = userRoles.includes('ServiceUser') || isStudentAuthenticated;

  const [bookings, setBookings] = useState([]);
  const [cancellationRequests, setCancellationRequests] = useState({});
  const [selectedBookings, setSelectedBookings] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');

  useEffect(() => {
    if (isServiceUser) {
      loadBookingsAndCancellations();
    }
  }, [isServiceUser]);

  const loadBookingsAndCancellations = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Load bookings and cancellation requests in parallel
      const [bookingsResponse, cancellationsResponse] = await Promise.all([
        bookingAPI.getMyBookings(getAccessTokenSilently),
        cancellationAPI.getMyCancellationRequests(getAccessTokenSilently)
      ]);

      if (bookingsResponse.success) {
        // Filter to only show upcoming bookings
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const upcomingBookings = (bookingsResponse.data || [])
          .filter(booking => {
            const bookingDate = new Date(booking.date);
            return bookingDate >= today;
          })
          .sort((a, b) => {
            // Sort by date, then by start time
            const dateCompare = new Date(a.date) - new Date(b.date);
            if (dateCompare !== 0) return dateCompare;
            return a.startTime.localeCompare(b.startTime);
          });
        
        setBookings(upcomingBookings);
      }

      if (cancellationsResponse.success) {
        // Create a map of bookingId -> cancellation request
        const cancellationMap = {};
        (cancellationsResponse.data || []).forEach(cancellation => {
          cancellationMap[cancellation.bookingId] = cancellation;
        });
        setCancellationRequests(cancellationMap);
      }
    } catch (err) {
      console.error('Failed to load bookings:', err);
      setError('Failed to load bookings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleBookingSelection = (bookingId) => {
    setSelectedBookings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bookingId)) {
        newSet.delete(bookingId);
      } else {
        newSet.add(bookingId);
      }
      return newSet;
    });
  };

  const handleRequestCancellation = () => {
    if (selectedBookings.size === 0) return;
    setShowReasonModal(true);
  };

  const submitCancellationRequest = async () => {
    if (selectedBookings.size === 0) return;

    setIsRequesting(true);
    setError(null);
    setSuccess(null);

    try {
      // Prepare cancellation requests
      const cancellations = Array.from(selectedBookings).map(bookingId => {
        const booking = bookings.find(b => b.bookingId === bookingId);
        return {
          bookingId: booking.bookingId,
          providerId: booking.providerId,
          instructorPhone: booking.instructorPhone,
          studentPhone: booking.studentPhone,
          date: booking.date,
          startTime: booking.startTime,
          endTime: booking.endTime,
          studentName: booking.studentName,
          providerName: booking.providerName,
          reason: cancellationReason.trim() || 'No reason provided',
          userId: booking.userId
        };
      });

      const response = await cancellationAPI.createMultipleCancellationRequests(
        { cancellations },
        getAccessTokenSilently
      );

      // NOTE: response.success here is "every request in the batch
      // succeeded" (set by the backend as failed.length === 0) — NOT "the
      // call itself completed". With one booking selected (the common
      // case), a single rejection — e.g. the cancel-before window below —
      // makes response.success false even though response.data is
      // present and valid, so this must not gate on response.success alone
      // or a fully-rejected request silently does nothing.
      if (response.data) {
        const { successCount, failCount, failed } = response.data;

        // Surface the actual reason(s) rather than just a count — the
        // cancel-before-window check is the most common cause of a
        // rejection a student will hit here.
        const reasons = [...new Set((failed || []).map(f => f.error).filter(Boolean))];
        const reasonText = reasons.length ? ` ${reasons.join(' ')}` : '';

        // Clear selections and close the modal first...
        setSelectedBookings(new Set());
        setCancellationReason('');
        setShowReasonModal(false);

        // ...and reload BEFORE setting the result message. loadBookingsAndCancellations()
        // calls setError(null) as its first line, so setting the message
        // before this await would get wiped out the instant the reload
        // starts, and the student would never see it.
        await loadBookingsAndCancellations();

        if (failCount === 0) {
          setSuccess(`Successfully requested cancellation for ${successCount} booking${successCount > 1 ? 's' : ''}!`);
        } else if (successCount === 0) {
          // Single booking (the common case) — show the backend's reason
          // on its own rather than burying it under a generic count message,
          // e.g. "Cancellation is not allowed within 48 hours of the
          // booking start time. Please contact your instructor directly
          // for this cancellation request immediately."
          setError(
            failCount === 1 && reasons.length === 1
              ? reasons[0]
              : `Could not request cancellation for ${failCount} booking${failCount > 1 ? 's' : ''}.${reasonText}`
          );
        } else {
          setSuccess(
            `Requested cancellation for ${successCount} booking${successCount > 1 ? 's' : ''}. ` +
            `${failCount} failed.${reasonText}`
          );
        }

        // Auto-hide messages
        setTimeout(() => { setSuccess(null); setError(null); }, 6000);
      }
    } catch (err) {
      console.error('Cancellation request error:', err);
      setError(`Failed to request cancellation: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsRequesting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatTime = (time24) => {
    const [hour, min] = time24.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${min.toString().padStart(2, '0')} ${period}`;
  };

  const getStatusBadge = (booking) => {
    const cancellation = cancellationRequests[booking.bookingId];
    
    if (!cancellation) {
      return <span className="status-badge status-confirmed">Confirmed</span>;
    }

    switch (cancellation.status) {
      case 'requested':
        return <span className="status-badge status-pending">Cancellation Requested</span>;
      case 'cancelled':
        return <span className="status-badge status-cancelled">Cancelled</span>;
      case 'denied':
        return <span className="status-badge status-denied">Cancellation Denied</span>;
      default:
        return <span className="status-badge status-confirmed">Confirmed</span>;
    }
  };

  const canRequestCancellation = (booking) => {
    const cancellation = cancellationRequests[booking.bookingId];
    // Can only request if no cancellation exists or if previous was denied
    return !cancellation || cancellation.status === 'denied';
  };

  if (!isServiceUser) {
    return (
      <div className="tab-content-placeholder">
        <h2>Access Denied</h2>
        <p>This page is only accessible to students</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bookings-container">
        <div className="loading-state">
          <Clock size={48} className="loading-icon" />
          <h2>Loading your bookings...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="bookings-container">
      {/* Success/Error Messages */}
      {success && (
        <div className="booking-success-banner">
          <CheckCircle size={24} />
          <span>{success}</span>
        </div>
      )}

      {error && (
        <div className="booking-error-banner">
          <AlertCircle size={24} />
          <span>{error}</span>
        </div>
      )}

      {/* Header with Actions */}
      <div className="bookings-header">
        <h2>My Bookings</h2>
        {selectedBookings.size > 0 && (
          <button 
            className="btn-request-cancellation"
            onClick={handleRequestCancellation}
            disabled={isRequesting}
          >
            Request Cancellation ({selectedBookings.size})
          </button>
        )}
      </div>

      {/* Bookings List */}
      {bookings.length === 0 ? (
        <div className="empty-state">
          <Calendar size={64} className="empty-icon" />
          <h3>No Upcoming Bookings</h3>
          <p>You don't have any upcoming bookings</p>
        </div>
      ) : (
        <div className="bookings-list">
          {bookings.map((booking) => {
            const cancellation = cancellationRequests[booking.bookingId];
            const isSelected = selectedBookings.has(booking.bookingId);
            const canCancel = canRequestCancellation(booking);

            return (
              <div 
                key={booking.bookingId} 
                className={`booking-card ${isSelected ? 'booking-card-selected' : ''} ${!canCancel ? 'booking-card-disabled' : ''}`}
              >
                {/* Checkbox */}
                {canCancel && (
                  <div className="booking-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleBookingSelection(booking.bookingId)}
                    />
                  </div>
                )}

                {/* Booking Details */}
                <div className="booking-details">
                  <div className="booking-header-row">
                    <div className="booking-date">
                      <Calendar size={18} />
                      <span>{formatDate(booking.date)}</span>
                    </div>
                    {getStatusBadge(booking)}
                  </div>

                  <div className="booking-time">
                    <Clock size={18} />
                    <span>{formatTime(booking.startTime)} - {formatTime(booking.endTime)}</span>
                  </div>

                  <div className="booking-instructor">
                    <User size={18} />
                    <span>Instructor: {booking.providerName}</span>
                  </div>

                  {/* Show cancellation details if exists */}
                  {cancellation && (
                    <div className="cancellation-info">
                      {cancellation.status === 'requested' && (
                        <p className="cancellation-note">
                          <AlertCircle size={16} />
                          Waiting for instructor approval
                        </p>
                      )}
                      {cancellation.status === 'denied' && cancellation.denialReason && (
                        <p className="cancellation-note cancellation-denied">
                          <XCircle size={16} />
                          Denied: {cancellation.denialReason}
                        </p>
                      )}
                      {cancellation.status === 'cancelled' && (
                        <p className="cancellation-note cancellation-approved">
                          <CheckCircle size={16} />
                          Cancellation approved
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reason Modal */}
      {showReasonModal && (
        <div className="modal-overlay" onClick={() => setShowReasonModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Request Cancellation</h3>
            <p>You are requesting cancellation for {selectedBookings.size} booking{selectedBookings.size > 1 ? 's' : ''}.</p>
            
            <div className="form-group">
              <label>Reason (optional):</label>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Provide a reason for cancellation..."
                rows={4}
                maxLength={500}
              />
            </div>

            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => {
                  setShowReasonModal(false);
                  setCancellationReason('');
                }}
                disabled={isRequesting}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={submitCancellationRequest}
                disabled={isRequesting}
              >
                {isRequesting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}